import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Strategy, StrategyDspFunnel } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { DspFunnelDescriptions } from "@front/m19-models";
import { DspStrategyEx } from "@front/m19-models/strategy/DspStrategyEx";
import { AccountSelectionService } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ModalService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  DspFunnelSelectorModalComponent,
  DspFunnelSelectorModalData,
} from "@m19-board/dsp/dsp-strategy-page/dsp-funnel-selector-modal.component";
import { combineLatest, map, switchMap } from "rxjs";

@Component({
  selector: "app-strategy-grid-dsp-funnel",
  imports: [IButtonDirective, TranslocoDirective, IIconComponent],
  template: `
    @if (dspStrategy(); as dspStrategy) {
      <button
        iButton
        class="group/funnel-btn"
        variant="ghost"
        [disabled]="dspStrategy.dspFunnel === StrategyDspFunnel.AWARENESS || isReadonly()"
        (click)="openFunnelSelectionModal(dspStrategy)"
        *transloco="let t"
      >
        {{ dspStrategy.dspFunnel ? t(DspFunnelDescriptions[dspStrategy.dspFunnel!].title) : "" }}
        <IIcon name="edit" class="opacity-0 group-hover/funnel-btn:opacity-100" />
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyGridDspFunnelComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyService = inject(StrategyService);
  readonly StrategyDspFunnel = StrategyDspFunnel;
  protected readonly DspFunnelDescriptions = DspFunnelDescriptions;

  isReadonly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, { requireSync: true });

  strategy = input.required<Strategy>();
  private dspStrategy$ = combineLatest([
    toObservable(this.strategy),
    this.accountSelectionService.singleAccountMarketplaceSelection$,
  ]).pipe(
    switchMap(([s, am]) =>
      this.strategyService
        .getStrategies(am.accountId, am.marketplace)
        .pipe(map((strategies) => strategies.get(s.strategyId!))),
    ),
    map((strategy) => (strategy && strategy.isDspStrategy() ? strategy : undefined)),
  );

  dspStrategy = toSignal(this.dspStrategy$);

  protected openFunnelSelectionModal(strategy: DspStrategyEx) {
    this.modalService.openModal<DspFunnelSelectorModalData, void>(DspFunnelSelectorModalComponent, {
      modalTitle: this.translocoService.translate("strategy-page.funnel_selection_modal_title"),
      data: {
        strategy: strategy,
        organizationId: this.accountMarketplace().resourceOrganizationId,
      },
    });
  }
}
